import { memo, useCallback } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Form } from 'src/shared/components/form/form.js';
import { Input } from 'src/shared/components/input/input.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { useSavedFieldLs } from 'src/shared/hooks/use-saved-field-ls.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { cnLoginResetPassword } from './login-reset-password.constants.js';

import './login-reset-password.css';

export interface LoginResetPasswordProps {
    kcContext: KcContext.LoginResetPassword;
}

export const LoginResetPassword = memo<LoginResetPasswordProps>(({ kcContext }) => {
    const { url } = kcContext;
    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const [onChangeSavedField, getSavedField] = useSavedFieldLs();

    const emailError = useFieldMessage(['email', 'username'], kcContext);
    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(['username'], {
        username: emailError,
    });

    const redirectToLogin = useCallback(() => {
        window.location.href = url.loginUrl;
    }, [url.loginUrl]);

    return (
        <Form
            action={url.loginAction}
            kcContext={kcContext}
            title={msgStr('resetPasswordTitle')}
            onSubmit={onSubmitValidated}
        >
            <Input
                type="text"
                id="email"
                name="username"
                label={msgStr('email')}
                autoComplete="email"
                autoFocus={!emailError}
                errorMessage={advancedMsgStr(formErrors['username'] ?? '')}
                onFocus={resetFieldError}
                onChange={onChangeSavedField}
                defaultValue={getSavedField('username')}
            />

            <Input type="submit" value={msgStr('resetPasswordButton')} disabled={submitDisabled} />

            <div className={cnLoginResetPassword('back-to-login')} onClick={redirectToLogin}>
                {msgStr('backToLogin')}
            </div>
        </Form>
    );
});
